import { AthleteDetail } from "../domain/AthleteDetail";

const CACHE_TTL_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

interface CachedAthleteDetail {
    data: AthleteDetail;
    cachedAt: number;
}

function getCacheKey(athleteId: number): string {
    return `athleteDetail_${athleteId}`;
}

function getFromCache(athleteId: number): AthleteDetail | null {
    try {
        const raw = localStorage.getItem(getCacheKey(athleteId));
        if (!raw) return null;
        const cached: CachedAthleteDetail = JSON.parse(raw);
        if (Date.now() - cached.cachedAt > CACHE_TTL_MS) {
            localStorage.removeItem(getCacheKey(athleteId));
            return null;
        }
        return cached.data;
    } catch {
        return null;
    }
}

function saveToCache(athleteId: number, data: AthleteDetail): void {
    try {
        const entry: CachedAthleteDetail = { data, cachedAt: Date.now() };
        localStorage.setItem(getCacheKey(athleteId), JSON.stringify(entry));
    } catch {
        // localStorage full or unavailable — silently skip caching
    }
}

export function getAthleteDetails(athleteId: number): Promise<AthleteDetail> {
    const cached = getFromCache(athleteId);
    if (cached) return Promise.resolve(cached);

    return fetch(`/api/athleteDetail?athleteId=${athleteId}`)
        .then(res => res.json())
        .then(data => {
            if (data?.name && Array.isArray(data.results)) {
                saveToCache(athleteId, data);
            }
            return data;
        });
}
